/**
 * Studio Course Query Hooks
 *
 * Symmetric to `studio-series.queries.ts`. Consumer-driven `refetchInterval`
 * for §11 polling during `pending_review`.
 */

import { useQuery } from '@tanstack/react-query';
import { studioCoursesClient } from '../client/studio-courses.client';
import { queryKeys } from '../query-keys';
import type {
  StudioCourseDetail,
  StudioCourseListFilters,
  StudioCourseListResponse,
} from '@/types/studio-courses';

export function useStudioCourses(
  filters: StudioCourseListFilters = {},
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    initialData?: StudioCourseListResponse;
  }
) {
  return useQuery({
    queryKey: queryKeys.studio.courses.list(filters as Record<string, unknown>),
    queryFn: () => studioCoursesClient.list(filters),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...(options?.refetchInterval !== undefined && { refetchInterval: options.refetchInterval }),
    ...(options?.initialData !== undefined && { initialData: options.initialData }),
  });
}

export function useStudioCourse(
  uuid: string | null | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    initialData?: StudioCourseDetail;
  }
) {
  return useQuery({
    queryKey: queryKeys.studio.courses.detail(uuid ?? ''),
    queryFn: () => studioCoursesClient.get(uuid!),
    enabled: !!uuid && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...(options?.refetchInterval !== undefined && { refetchInterval: options.refetchInterval }),
    ...(options?.initialData !== undefined && { initialData: options.initialData }),
  });
}
